import {FlowStatePresentation} from "@nolimitcity/slot-game/bin/flowcontrol/FlowStatePresentation";
import {FlowStateController} from "@nolimitcity/slot-game/bin/flowcontrol/FlowStateController";
import {KennethMustDie} from "../KennethMustDie";
import {gsap} from "gsap";
import {KMDGameMode} from "../server/KMDGameMode";
import {IBetWayWinData, KMD_SYMBOL_ANIMATIONS, SPECIAL_SYMBOL_NAMES, X_NUDGE_ANIMATIONS} from "../server/IKMDData";
import {SimpleReelModel, SimpleReelModelSymbolData} from "../../core/reel/simplereel/SimpleReelModel";
import {KMDSymbol} from "../reels/KMDSymbol";

export class KMDNormalIdlePresentation implements FlowStatePresentation {
    private _tl: gsap.core.Timeline;
    private _symInteractiveTl: gsap.core.Timeline;
    private _noPlaySoundTl: gsap.core.Timeline;
    private _flowStateController: FlowStateController;

    public start(flowStateController: FlowStateController): void {
        flowStateController.setSkipCallback(() => this.skip());
        KennethMustDie.car.playCar();

        this._flowStateController = flowStateController;

        if (KennethMustDie.autoPlayPlugin.isAutoplayRound) {
            return;
        }

        // #225
        this._noPlaySoundTl = gsap.timeline();
        this._noPlaySoundTl.add(() => {
            KennethMustDie.soundPlayer.switchMusic("noPlay", {
                name: "",
                to: 0,
                duration: 8000
            }, {
                name: "",
                from: 0,
                to: 1,
                duration: 8000
            });
        }, 30);

        if (KennethMustDie.model.gameData?.nextMode !== KMDGameMode.NORMAL) {
            return;
        }

        KennethMustDie.xBet.enable(true, 0.3);
        KennethMustDie.limitCapButton.enable(true, 0.3);

        if (!KennethMustDie.model.gameData || KennethMustDie.model.gameData.brokeTheBank) {
            return;
        }

        // #345, #362
        if (KennethMustDie.isActionSpinsActive || KennethMustDie.isSingleWinBelowStake()) {
            return;
        }

        if (KennethMustDie.model.gameData.betWayWins.length > 0 || KennethMustDie.model.specialBetWayWins.length > 0) { // #371, #394, condition modified
            const betWays: IBetWayWinData[] = KennethMustDie.model.gameData.betWayWins.concat(KennethMustDie.model.specialBetWayWins);

            this._tl = gsap.timeline({
                repeat: -1,
                delay: 1
            });

            betWays.forEach((betWay: IBetWayWinData) => {
                const tls: gsap.core.Timeline[] = [],
                    toggleSymName: string = betWay.symbol;

                this._tl.add(() => {
                    KennethMustDie.toggle.showToggleAmount(betWay.multipliedAmount);
                });
                betWay.reelWinPositions.forEach((value: boolean[], reelId: number) => {
                    value.forEach((val: boolean, symId: number) => {
                        if (val) {
                            const symbolAnim: gsap.core.Timeline = gsap.timeline(),
                                reel: SimpleReelModel = (symId === 3 && reelId > 0 && reelId < 4) ? KennethMustDie.specialReelView.reel : KennethMustDie.reelView.reelModels[reelId],
                                effectiveSymId: number = (reelId > 0 && reelId < 4 ? (reel.id === 5 ? (reelId - 1) : symId) : (symId - 1)),
                                symbol: SimpleReelModelSymbolData = reel.getStopSymbolAt(effectiveSymId),
                                sym: KMDSymbol = (symbol.positionable as KMDSymbol);

                            if (sym.originalName === SPECIAL_SYMBOL_NAMES.X_NUDGE_WILD) {
                                let toggleState: string = KMD_SYMBOL_ANIMATIONS.TOGGLE;

                                if (effectiveSymId === 2) {
                                    toggleState = X_NUDGE_ANIMATIONS.FULL_TOGGLE_2;
                                }

                                symbolAnim.add(sym.setState(toggleState));
                            } else if (KennethMustDie.model.gameData.evaluatedArea[reelId][symId] !== SPECIAL_SYMBOL_NAMES.NW) {
                                symbolAnim.add([
                                    () => {
                                        sym.removeNoWinTint();

                                        // #394
                                        if (sym.name !== SPECIAL_SYMBOL_NAMES.W) {
                                            sym.updateTexture(toggleSymName, sym.splitParts);
                                        }
                                    },
                                    sym.setToggleState()
                                ]);
                            }

                            // #231
                            tls.push(symbolAnim.timeScale(0.5));
                        } else {
                            const symbolAnim: gsap.core.Timeline = gsap.timeline(),
                                reel: SimpleReelModel = (symId === 3 && reelId > 0 && reelId < 4) ? KennethMustDie.specialReelView.reel : KennethMustDie.reelView.reelModels[reelId],
                                effectiveSymId: number = (reelId > 0 && reelId < 4 ? (reel.id === 5 ? (reelId - 1) : symId) : (symId - 1)),
                                symbol: SimpleReelModelSymbolData = reel.getStopSymbolAt(effectiveSymId);

                            if (symbol) {
                                const sym: KMDSymbol = symbol.positionable as KMDSymbol;

                                if (sym.getExtrasLength()) {
                                    symbolAnim.add(() => {
                                        KennethMustDie.xWaysReveal.addNoWinTint(reelId);
                                    });
                                }

                                symbolAnim.add(() => {
                                    sym.getNoWinStateAnimation();
                                });
                                tls.push(symbolAnim);
                            }
                        }
                    });
                });

                this._tl.add(tls);
            });

            if (betWays.length > 1) {
                this._tl.add(() => {
                    KennethMustDie.reelView.reelModels.forEach((reel: SimpleReelModel, reelId: number) => {
                        reel.stopSymbols.forEach((sym: SimpleReelModelSymbolData) => {
                            const symbol: KMDSymbol = (sym.positionable as KMDSymbol);

                            if (symbol.getExtrasLength()) {
                                KennethMustDie.xWaysReveal.removeNoWinTint(reelId);
                            }

                            symbol.removeNoWinTint();
                        });
                    });
                    KennethMustDie.specialReelView.reel.stopSymbols.forEach((sym: SimpleReelModelSymbolData, symId: number) => {
                        const symbol: KMDSymbol = (sym.positionable as KMDSymbol);

                        if (symbol.getExtrasLength()) {
                            KennethMustDie.xWaysReveal.removeNoWinTint(symId + 1);
                        }

                        symbol.removeNoWinTint();
                    });

                    KennethMustDie.toggle.hideToggleAmount();
                });
                this._tl.to(this, {
                    duration: 2
                });
            }
        } else {
            // #331
            this._symInteractiveTl = gsap.timeline({
                delay: 0.2
            });

            this._symInteractiveTl.add(() => {
                KennethMustDie.reelView.reelModels.forEach((reel: SimpleReelModel) => {
                    reel.setInteractiveSymbols(true);
                });
                KennethMustDie.specialReelView.reel.setInteractiveSymbols(true);
            });
        }
    }

    // #345
    public stop(): void {
        if (this._tl !== undefined) {
            this._tl.kill();
            KennethMustDie.toggle.hideToggleAmount();
        }
    }

    public skip(): void {
        this.stop();

        if (this._symInteractiveTl !== undefined) {
            this._symInteractiveTl.kill();
        }

        // #225
        if (this._noPlaySoundTl) {
            this._noPlaySoundTl.pause();
            this._noPlaySoundTl.kill();

            KennethMustDie.soundPlayer.switchMusic(KMDGameMode.NORMAL);
            KennethMustDie.soundPlayer.killBgFadeOutTl();
        }

        if (this._flowStateController) {
            this._flowStateController.finish();
        }
    }
}